/**
 * EXPENSES HOOK - API Methods & React Query Hooks Summary
 *
 * ===========================================
 * API METHODS (Internal Functions):
 * ===========================================
 * • getExpenses()              - GET /expenses - Fetch paginated expenses with filters
 * • getExpenseDetails()        - GET /expenses/:id - Fetch specific expense details
 * • createExpense()            - POST /expenses - Create a new expense with receipt
 * • cancelExpense()            - PATCH /expenses/:id/cancel - Cancel a pending expense
 * • payExpense()               - POST /expenses/:id/approve - Approve a single expense
 * • getExpensePayerWallets()   - GET /expenses/:id/payer-wallets - Wallets that can fund an expense
 * • bulkDeclineExpenses()      - PATCH /expenses/bulk-update - Decline multiple expenses
 * • bulkPayExpenses()          - POST /expenses/bulk-approve - Approve multiple expenses in bulk
 *
 * ===========================================
 * REACT QUERY HOOKS (Exported):
 * ===========================================
 * • useExpenses(filters?, enabled?)           - Query: Get paginated expenses with filters
 * • useExpenseDetails(expenseId, enabled?)    - Query: Get expense by ID
 * • useFetchExpenseDetails()                  - Mutation: Manually fetch expense details
 * • useCreateExpense()                        - Mutation: Create a new expense
 * • useCancelExpense()                        - Mutation: Cancel a pending expense
 * • usePayExpense()                           - Mutation: Pay a single expense
 * • useExpensePayerWallets(expenseId, enabled?) - Query: Wallets that can fund an expense
 * • useBulkDeclineExpenses()                  - Mutation: Decline multiple expenses
 * • useBulkPayExpenses()                      - Mutation: Pay multiple expenses in bulk
 *
 * ===========================================
 * KEY FEATURES:
 * ===========================================
 * • Authentication via Bearer token (store + cookie fallback)
 * • Comprehensive filtering (status, category, currency, user, team_member_ids, dates, search, sort)
 * • Support for expense statuses: 'pending', 'paid', 'declined', 'cancelled'
 * • Support for expense categories: Travel expenses, Meals, Office supplies, etc.
 * • Multipart form data support for receipt file uploads
 * • Bulk operations for decline and pay actions
 * • Currency sum calculation when filtering by currency code
 * • 5-minute stale time with disabled auto-refetch
 * • Automatic query invalidation on mutations
 * ===========================================
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  BulkDeclineRequest,
  Expense,
  ExpenseCategoryOption,
  ExpenseFilters,
  ExpenseStatus,
  ExpenseSubmissionWindow,
  PayExpenseResponse,
  ReimbursementMethod,
} from '@workspace/ui/lib';
import {
  buildQueryParams,
  EXPENSE_STATUSES,
  FALLBACK_EXPENSE_SUBMISSION_WINDOW,
  getExpenseSubmissionWindowQueryKey,
  REIMBURSEMENT_METHODS,
} from '@workspace/ui/lib';
import type { Currency } from '@workspace/ui/types';
import { getExpensePeriodDateRange } from '@/lib/expenses-utils';
import fetchAPI from '@/lib/fetch-api';
import type {
  BulkPayRequest,
  BulkPayResponse,
  CancelExpenseRequest,
  CreateExpenseRequest,
  CreatePrepaidExpenseRequest,
  ExpenseApprovedTotalsData,
  ExpenseApprovedTotalsFilters,
  ExpenseCategoryBreakdownData,
  ExpenseCategoryBreakdownFilters,
  ExpenseCurrenciesFilters,
  ExpenseCurrenciesListData,
  ExpenseDetailsParams,
  ExpensePayerWallet,
  ExpensePendingTotalsCurrency,
  ExpensePendingTotalsData,
  ExpensePendingTotalsFilters,
  PayExpenseRequest,
  ReviewExpenseReceiptRequest,
  SubmitExpenseReceiptRequest,
} from '@/types/expense';

export type {
  ExpenseCategoryOption,
  ExpensePendingTotalsCurrency,
  ExpensePendingTotalsData,
  ExpensePendingTotalsFilters,
  ExpenseStatus,
  ReimbursementMethod,
};
// Re-export constants and types for convenience
export { EXPENSE_STATUSES, REIMBURSEMENT_METHODS };

// ==================== INTERFACES ====================

interface ExpenseListData {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  has_more: boolean;
  items: Expense[];
  currency_sum?: number;
}

// ==================== API FUNCTIONS ====================

/**
 * Fetches a paginated list of expenses with optional filters
 * @param {ExpenseFilters} filters - Optional filters for the expense list
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<ExpenseListData>} The paginated expense list
 */
const getExpenses = async (
  filters: ExpenseFilters = {},
  organisationId?: number
): Promise<ExpenseListData> => {
  const searchParams = new URLSearchParams();

  if (filters.page) searchParams.append('current_page', filters.page.toString());
  if (filters.per_page) searchParams.append('per_page', filters.per_page.toString());
  if (filters.status && filters.status.length > 0) {
    filters.status.forEach((status) => searchParams.append('status[]', status));
  }
  if (filters.category) searchParams.append('category', filters.category);
  if (filters.expense_type) searchParams.append('expense_type', filters.expense_type);
  if (filters.prepaid_receipt_status && filters.prepaid_receipt_status.length > 0) {
    filters.prepaid_receipt_status.forEach((status) =>
      searchParams.append('prepaid_receipt_status[]', status)
    );
  }
  if (filters.exclude_prepaid_receipt_status && filters.exclude_prepaid_receipt_status.length > 0) {
    filters.exclude_prepaid_receipt_status.forEach((status) =>
      searchParams.append('exclude_prepaid_receipt_status[]', status)
    );
  }
  if (filters.currency_id) searchParams.append('currency_id', filters.currency_id.toString());
  if (filters.currency) searchParams.append('currency', filters.currency);
  if (filters.user_id) searchParams.append('user_id', filters.user_id.toString());
  const organisationIdForQuery = organisationId ?? filters.organisation_id;
  if (organisationIdForQuery != null && organisationIdForQuery > 0) {
    searchParams.append('organisation_id', organisationIdForQuery.toString());
  }
  if (filters.team_member_ids && filters.team_member_ids.length > 0) {
    filters.team_member_ids.forEach((id) =>
      searchParams.append('team_member_ids[]', id.toString())
    );
  }
  if (filters.organisation_ids && filters.organisation_ids.length > 0) {
    filters.organisation_ids.forEach((id) =>
      searchParams.append('organisation_ids[]', id.toString())
    );
  }
  if (filters.request_date_from)
    searchParams.append('request_date_from', filters.request_date_from);
  if (filters.request_date_to) searchParams.append('request_date_to', filters.request_date_to);
  if (filters.search) searchParams.append('search', filters.search);
  if (filters.reimbursement_method)
    searchParams.append('reimbursement_method', filters.reimbursement_method);
  if (filters.team_id) searchParams.append('team_id', filters.team_id.toString());
  if (filters.view_self_inputs !== undefined)
    searchParams.append('view_self_inputs', filters.view_self_inputs.toString());
  if (filters.sort) {
    searchParams.append('sort[by]', filters.sort.by);
    searchParams.append('sort[order]', filters.sort.order);
  }

  const queryString = searchParams.toString();
  const url = `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses${queryString ? `?${queryString}` : ''}`;

  // Response structure: { success, data: Expense[], pagination: {...}, currency_sum?: number }
  const response = await fetchAPI<Expense[]>(url, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch expenses');
  }

  if (!response.data || !response.pagination) {
    throw new Error('Invalid response format: missing data or pagination');
  }

  // API returns camelCase pagination, normalize to snake_case for consistency
  return {
    current_page: response.pagination.current_page,
    per_page: response.pagination.per_page,
    total: response.pagination.total,
    last_page: response.pagination.last_page,
    has_more: response.pagination.current_page < response.pagination.last_page,
    items: response.data,
    currency_sum: (response as { currency_sum?: number }).currency_sum,
  };
};

/**
 * Retrieves detailed information about a specific expense
 * @param {number} expenseId - The expense identifier
 * @param {ExpenseDetailsParams} params - Optional organisation/team scope for the request
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<Expense>} The expense details
 */
const getExpenseDetails = async (
  expenseId: number,
  params: ExpenseDetailsParams = {}
): Promise<Expense> => {
  if (!expenseId) {
    throw new Error('Expense ID is required');
  }

  const searchParams = buildQueryParams({
    organisation_id: params.organisation_id,
    team_id: params.team_id,
  });
  const queryString = searchParams.toString();
  const url = `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses/${expenseId}${queryString ? `?${queryString}` : ''}`;

  const response = await fetchAPI<Expense>(url, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch expense details');
  }

  return response.data!;
};

const getExpenseSubmissionWindow = async (params: {
  organisationId: number;
  userId?: number;
}): Promise<ExpenseSubmissionWindow> => {
  const queryParams = buildQueryParams({
    organisation_id: params.organisationId,
    user_id: params.userId,
  });

  try {
    const response = await fetchAPI<ExpenseSubmissionWindow>(
      `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses/submission-window?${queryParams.toString()}`,
      { method: 'GET' }
    );

    return response.data ?? FALLBACK_EXPENSE_SUBMISSION_WINDOW;
  } catch {
    return FALLBACK_EXPENSE_SUBMISSION_WINDOW;
  }
};

/**
 * Creates a new expense with receipt file upload
 * @param {CreateExpenseRequest} expenseData - The expense data with receipt file
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<Expense>} The created expense
 */
const createExpense = async (expenseData: CreateExpenseRequest): Promise<Expense> => {
  const formData = new FormData();
  formData.append('organisation_id', expenseData.organisation_id.toString());
  formData.append('amount', expenseData.amount.toString());
  formData.append('currency_id', expenseData.currency_id.toString());
  formData.append('category', expenseData.category);
  formData.append('description', expenseData.description);
  formData.append('purchase_date', expenseData.purchase_date);
  formData.append('country_id', expenseData.country_id.toString());
  formData.append('reimbursement_method', expenseData.reimbursement_method);
  if (expenseData.user_id) {
    formData.append('user_id', expenseData.user_id.toString());
  }
  if (expenseData.team_id) {
    formData.append('team_id', expenseData.team_id.toString());
  }
  if (expenseData.payroll_date) {
    formData.append('payroll_date', expenseData.payroll_date ?? '');
  }
  if (expenseData.wallet_currency_id) {
    formData.append('wallet_currency_id', expenseData.wallet_currency_id.toString());
  }
  if (expenseData.note) {
    formData.append('note', expenseData.note);
  }
  // Append multiple files
  expenseData.files.forEach((file, index) => {
    formData.append(`files[${index}]`, file);
  });
  const response = await fetchAPI<Expense>(`${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses`, {
    method: 'POST',
    body: formData,
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to create expense');
  }

  return response.data!;
};

/**
 * Creates a new pre-paid expense (upfront fund allocation, employee self-create only)
 * @param {CreatePrepaidExpenseRequest} expenseData - The pre-paid expense data
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<Expense>} The created pre-paid expense
 */
const createPrepaidExpense = async (expenseData: CreatePrepaidExpenseRequest): Promise<Expense> => {
  const formData = new FormData();
  formData.append('organisation_id', expenseData.organisation_id.toString());
  formData.append('description', expenseData.description);
  formData.append('expense_name', expenseData.description);
  formData.append('amount', expenseData.amount.toString());
  formData.append('currency_id', expenseData.currency_id.toString());
  formData.append('reimbursement_method', expenseData.reimbursement_method);
  if (expenseData.purchase_date) {
    formData.append('purchase_date', expenseData.purchase_date);
  }
  if (expenseData.merchant_name) {
    formData.append('merchant_name', expenseData.merchant_name);
  }
  if (expenseData.note) {
    formData.append('note', expenseData.note);
  }
  if (expenseData.country_id) {
    formData.append('country_id', expenseData.country_id.toString());
  }
  if (expenseData.category) {
    formData.append('category', expenseData.category);
  }
  if (expenseData.wallet_currency_id) {
    formData.append('wallet_currency_id', expenseData.wallet_currency_id.toString());
  }
  if (expenseData.files) {
    expenseData.files.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });
  }

  const response = await fetchAPI<Expense>(
    `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses/prepaid`,
    {
      method: 'POST',
      body: formData,
    }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to create pre-paid expense');
  }

  return response.data!;
};

/**
 * Submits receipts and the actual spent amount for an approved pre-paid expense
 * @param {number} expenseId - The pre-paid expense identifier
 * @param {SubmitExpenseReceiptRequest} receiptData - The receipt amount, category and files
 * @throws {Error} If the API request fails
 * @returns {Promise<Expense>} The updated expense
 */
const submitExpenseReceipt = async (
  expenseId: number,
  receiptData: SubmitExpenseReceiptRequest
): Promise<Expense> => {
  const formData = new FormData();
  formData.append('receipt_amount', receiptData.receipt_amount.toString());
  formData.append('category', receiptData.category);
  if (receiptData.merchant_name) {
    formData.append('merchant_name', receiptData.merchant_name);
  }
  if (receiptData.country_id) {
    formData.append('country_id', receiptData.country_id.toString());
  }
  if (receiptData.purchase_date) {
    formData.append('purchase_date', receiptData.purchase_date);
  }
  if (receiptData.note) {
    formData.append('note', receiptData.note);
  }
  if (receiptData.reimbursement_method) {
    formData.append('reimbursement_method', receiptData.reimbursement_method);
  }
  if (receiptData.wallet_currency_id) {
    formData.append('wallet_currency_id', receiptData.wallet_currency_id.toString());
  }
  receiptData.files.forEach((file, index) => {
    formData.append(`files[${index}]`, file);
  });

  const response = await fetchAPI<Expense>(
    `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses/${expenseId}/receipt`,
    {
      method: 'POST',
      body: formData,
    }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to submit receipt');
  }

  return response.data!;
};

/**
 * Cancels a pending expense
 * @param {number} expenseId - The expense identifier to cancel
 * @throws {Error} If the expense cannot be cancelled or the API request fails
 * @returns {Promise<Expense>} The cancelled expense
 */
const cancelExpense = async ({
  expenseId,
  team_id,
}: { expenseId: number } & CancelExpenseRequest): Promise<Expense> => {
  if (!expenseId) {
    throw new Error('Expense ID is required');
  }

  const body = team_id !== undefined ? JSON.stringify({ team_id }) : undefined;

  const response = await fetchAPI<Expense>(
    `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses/${expenseId}/cancel`,
    {
      method: 'PATCH',
      ...(body ? { body } : {}),
    }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to cancel expense');
  }

  return response.data!;
};

/**
 * Wallets of the organisation that pays for an expense. The caller picks `wallet_currency_id`
 * from this list. Balances are null for approvers who are not the payer; use `has_sufficient_funds`.
 * Throws the API message, including the 422 returned when the organisation has no employer.
 */
const getExpensePayerWallets = async (expenseId: number): Promise<ExpensePayerWallet[]> => {
  if (!expenseId) {
    throw new Error('Expense ID is required');
  }

  const response = await fetchAPI<ExpensePayerWallet[]>(
    `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses/${expenseId}/payer-wallets`,
    { method: 'GET' }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to load payer wallets');
  }

  return Array.isArray(response.data) ? response.data : [];
};

/**
 * Approves a single expense
 * @param {number} expenseId - The expense identifier to approve
 * @param {PayExpenseRequest} payData - The approval data including reimbursement method and optional wallet currency
 * @throws {Error} If the expense cannot be approved or the API request fails
 * @returns {Promise<PayExpenseResponse>} The approval response
 */
const payExpense = async (
  expenseId: number,
  payData: PayExpenseRequest
): Promise<PayExpenseResponse> => {
  if (!expenseId) {
    throw new Error('Expense ID is required');
  }

  const response = await fetchAPI<PayExpenseResponse>(
    `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses/${expenseId}/approve`,
    {
      method: 'POST',
      body: JSON.stringify(payData),
    }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to approve expense');
  }

  return response.data!;
};

/**
 * Bulk decline multiple expenses
 * @param {BulkDeclineRequest} declineData - The decline request with expense IDs and note
 * @throws {Error} If the expenses cannot be declined or the API request fails
 * @returns {Promise<Expense[]>} The declined expenses
 */
const bulkDeclineExpenses = async (declineData: BulkDeclineRequest): Promise<Expense[]> => {
  if (!declineData.ids || declineData.ids.length === 0) {
    throw new Error('At least one expense ID is required');
  }

  const response = await fetchAPI<Expense[]>(
    `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses/bulk-decline`,
    {
      method: 'PATCH',
      body: JSON.stringify(declineData),
    }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to decline expenses');
  }

  return response.data!;
};

/**
 * Bulk approve multiple expenses
 * @param {BulkPayRequest} payData - The bulk approve request with expense IDs and optional wallet currency
 * @throws {Error} If the expenses cannot be approved or the API request fails
 * @returns {Promise<BulkPayResponse>} The bulk approval response
 */
const bulkPayExpenses = async (payData: BulkPayRequest): Promise<BulkPayResponse> => {
  if (!payData.ids || payData.ids.length === 0) {
    throw new Error('At least one expense ID is required');
  }

  const response = await fetchAPI<BulkPayResponse>(
    `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses/bulk-approve`,
    {
      method: 'POST',
      body: JSON.stringify(payData),
    }
  );

  if (!response.success && (!response.status || ![200, 201, 202].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to approve expenses');
  }

  return response.data!;
};

/**
 * Reviews submitted receipts for a pre-paid expense (approve or decline)
 * @param {number} expenseId - The pre-paid expense identifier
 * @param {ReviewExpenseReceiptRequest} reviewData - action + optional decline_note
 * @throws {Error} If the API request fails
 * @returns {Promise<Expense>} The updated expense
 */
const reviewExpenseReceipt = async (
  expenseId: number,
  reviewData: ReviewExpenseReceiptRequest
): Promise<Expense> => {
  if (!expenseId) {
    throw new Error('Expense ID is required');
  }

  const response = await fetchAPI<Expense>(
    `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses/${expenseId}/receipt-review`,
    {
      method: 'POST',
      body: JSON.stringify(reviewData),
    }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to review receipt');
  }

  return response.data!;
};

/**
 * Sends a reminder to the employee to submit receipts for an approved pre-paid expense
 * @param {number} expenseId - The pre-paid expense identifier
 * @throws {Error} If the API request fails (including 429 rate limit)
 */
const sendReceiptReminder = async (expenseId: number): Promise<void> => {
  if (!expenseId) {
    throw new Error('Expense ID is required');
  }

  const response = await fetchAPI(
    `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses/${expenseId}/send-receipt-reminder`,
    { method: 'POST' }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to send reminder');
  }
};

/**
 * Fetches all available expense categories
 * @throws {Error} If the API request fails
 * @returns {Promise<ExpenseCategoryOption[]>} The list of expense categories
 */
const getExpenseCategories = async (): Promise<ExpenseCategoryOption[]> => {
  const response = await fetchAPI<ExpenseCategoryOption[]>(
    `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses/categories`,
    { method: 'GET' }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch expense categories');
  }

  return response.data!;
};

/**
 * Fetches expense currencies with optional filters
 * @param {ExpenseCurrenciesFilters} filters - Optional filters for expense currencies
 * @throws {Error} If the API request fails
 * @returns {Promise<ExpenseCurrenciesListData>} The paginated expense currencies
 */
const getExpenseCurrencies = async (
  filters: ExpenseCurrenciesFilters = {}
): Promise<ExpenseCurrenciesListData> => {
  const searchParams = buildQueryParams({
    current_page: filters.current_page,
    per_page: filters.per_page,
    organisation_id: filters.organisation_id,
    team_id: filters.team_id,
    scope: filters.scope,
    search: filters.search,
    view_self_inputs: filters.view_self_inputs,
    sort: filters.sort,
  });

  const queryString = searchParams.toString();
  const url = `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses/currencies${queryString ? `?${queryString}` : ''}`;

  const response = await fetchAPI<Currency[]>(url, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch expense currencies');
  }

  if (!response.data || !response.pagination) {
    throw new Error('Invalid response format: missing data or pagination');
  }

  return {
    current_page: response.pagination.current_page,
    per_page: response.pagination.per_page,
    total: response.pagination.total,
    last_page: response.pagination.last_page,
    has_more: response.pagination.current_page < response.pagination.last_page,
    items: response.data,
  };
};

/**
 * Fetches pending expense totals grouped by currency
 * @param {ExpensePendingTotalsFilters} filters - Optional filters
 * @throws {Error} If the API request fails
 * @returns {Promise<ExpensePendingTotalsData>} Per-currency pending totals
 */
const getExpensePendingTotals = async (
  filters: ExpensePendingTotalsFilters = {}
): Promise<ExpensePendingTotalsData> => {
  const searchParams = buildQueryParams({
    organisation_id: filters.organisation_id,
    team_id: filters.team_id,
    currency: filters.currency,
    view_self_inputs: filters.view_self_inputs,
    sort: filters.sort,
  });

  const queryString = searchParams.toString();
  const url = `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses/pending-totals${queryString ? `?${queryString}` : ''}`;

  const response = await fetchAPI<ExpensePendingTotalsData>(url, { method: 'GET' });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch pending totals');
  }

  return response.data!;
};

/**
 * Fetches approved expense totals grouped by category for a rolling period window
 * @param {ExpenseCategoryBreakdownFilters} filters - Period, currency and scope filters
 * @throws {Error} If the API request fails
 * @returns {Promise<ExpenseCategoryBreakdownData>} Category breakdown totals
 */
const getExpenseCategoryBreakdown = async (
  filters: ExpenseCategoryBreakdownFilters
): Promise<ExpenseCategoryBreakdownData> => {
  const searchParams = buildQueryParams({
    period: filters.period,
    currency: filters.currency,
    currency_id: filters.currency_id,
    organisation_id: filters.organisation_id,
    team_id: filters.team_id,
    view_self_inputs: filters.view_self_inputs,
  });

  const queryString = searchParams.toString();
  const url = `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses/category-breakdown${queryString ? `?${queryString}` : ''}`;

  const response = await fetchAPI<ExpenseCategoryBreakdownData>(url, { method: 'GET' });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch category breakdown');
  }

  return response.data!;
};

/**
 * Exports approved expense totals grouped by category as a CSV file
 * @param {ExpenseCategoryBreakdownFilters} filters - Period, currency and scope filters
 * @throws {Error} If the API request fails
 * @returns {Promise<Blob>} CSV file blob
 */
const getExpenseCategoryBreakdownExport = async (
  filters: ExpenseCategoryBreakdownFilters
): Promise<Blob> => {
  const searchParams = buildQueryParams({
    period: filters.period,
    currency: filters.currency,
    currency_id: filters.currency_id,
    organisation_id: filters.organisation_id,
    team_id: filters.team_id,
    view_self_inputs: filters.view_self_inputs,
  });

  const queryString = searchParams.toString();
  const url = `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses/category-breakdown/export${queryString ? `?${queryString}` : ''}`;

  const response = await fetchAPI<Blob>(url, { method: 'GET', responseType: 'blob' });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to export category breakdown');
  }

  return response.data!;
};

/**
 * Fetches approved expense totals grouped by month for a rolling period window
 * @param {ExpenseApprovedTotalsFilters} filters - Period, currency and scope filters
 * @throws {Error} If the API request fails
 * @returns {Promise<ExpenseApprovedTotalsData>} Monthly approved totals
 */
const getExpenseApprovedTotals = async (
  filters: ExpenseApprovedTotalsFilters
): Promise<ExpenseApprovedTotalsData> => {
  const searchParams = buildQueryParams({
    period: filters.period,
    year: filters.year,
    currency: filters.currency,
    currency_id: filters.currency_id,
    organisation_id: filters.organisation_id,
    team_id: filters.team_id,
    view_self_inputs: filters.view_self_inputs,
  });

  const queryString = searchParams.toString();
  const url = `${process.env.NEXT_PUBLIC_EOR_API_URL}/expenses/approved-totals${queryString ? `?${queryString}` : ''}`;

  const response = await fetchAPI<ExpenseApprovedTotalsData>(url, { method: 'GET' });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch approved totals');
  }

  return response.data!;
};

// ==================== REACT QUERY HOOKS ====================

/**
 * Hook to fetch all available expense categories
 * @returns {UseQueryResult} Query result for expense categories
 */
export function useExpenseCategories(): UseQueryResult<ExpenseCategoryOption[], Error> {
  return useQuery({
    queryKey: ['expense-categories'],
    queryFn: getExpenseCategories,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

/**
 * Hook to fetch expense currencies with optional filters
 * @param {ExpenseCurrenciesFilters} filters - Optional filters for expense currencies
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for expense currencies list
 */
export function useExpenseCurrencies(
  filters: ExpenseCurrenciesFilters = {},
  enabled: boolean = true
): UseQueryResult<ExpenseCurrenciesListData, Error> {
  const queryKey = [
    'expense-currencies',
    {
      current_page: filters.current_page,
      per_page: filters.per_page,
      organisation_id: filters.organisation_id,
      team_id: filters.team_id,
      scope: filters.scope,
      search: filters.search,
      view_self_inputs: filters.view_self_inputs,
      sort: filters.sort,
    },
  ];

  return useQuery({
    queryKey,
    queryFn: () => getExpenseCurrencies(filters),
    enabled,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

/**
 * Hook to fetch a paginated list of expenses with optional filters
 * @param {ExpenseFilters} filters - Optional filters for the expense list
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for expenses list
 */
export function useExpenses(
  filters: ExpenseFilters = {},
  enabled: boolean = true,
  organisationId?: number
): UseQueryResult<ExpenseListData, Error> {
  const resolvedOrganisationId = organisationId ?? filters.organisation_id;

  const queryKey = [
    'expenses',
    {
      page: filters.page,
      per_page: filters.per_page,
      status: filters.status,
      category: filters.category,
      expense_type: filters.expense_type,
      prepaid_receipt_status: filters.prepaid_receipt_status,
      exclude_prepaid_receipt_status: filters.exclude_prepaid_receipt_status,
      currency_id: filters.currency_id,
      currency: filters.currency,
      user_id: filters.user_id,
      organisation_id: resolvedOrganisationId,
      team_member_ids: filters.team_member_ids,
      organisation_ids: filters.organisation_ids,
      request_date_from: filters.request_date_from,
      request_date_to: filters.request_date_to,
      search: filters.search,
      reimbursement_method: filters.reimbursement_method,
      team_id: filters.team_id,
      view_self_inputs: filters.view_self_inputs,
      sort: filters.sort,
    },
  ];

  return useQuery({
    queryKey,
    queryFn: () => getExpenses(filters, organisationId ?? filters.organisation_id),
    enabled,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

/**
 * Hook to fetch expense details for a specific expense ID
 * @param {number} expenseId - The expense ID to fetch details for
 * @param {boolean} enabled - Whether the query should be enabled
 * @param {ExpenseDetailsParams} params - Optional organisation/team scope for the request
 * @returns {UseQueryResult} Query result for expense details
 */
export function useExpenseDetails(
  expenseId: number,
  enabled: boolean = true,
  params: ExpenseDetailsParams = {}
): UseQueryResult<Expense, Error> {
  return useQuery({
    queryKey: ['expense-details', expenseId, params.organisation_id, params.team_id],
    queryFn: () => getExpenseDetails(expenseId, params),
    enabled: enabled && !!expenseId,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

interface FetchExpenseDetailsVariables extends ExpenseDetailsParams {
  expenseId: number;
}

/**
 * Hook to fetch expense details (as a mutation for manual triggering)
 * @returns {UseMutationResult} Mutation result for fetching expense details
 */
export function useFetchExpenseDetails(): UseMutationResult<
  Expense,
  Error,
  FetchExpenseDetailsVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expenseId, organisation_id, team_id }) =>
      getExpenseDetails(expenseId, { organisation_id, team_id }),
    onSuccess: (data, { expenseId, organisation_id, team_id }) => {
      queryClient.setQueryData(['expense-details', expenseId, organisation_id, team_id], data);
      queryClient.invalidateQueries({ queryKey: ['expense-details'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
}

export function useExpenseSubmissionWindow({
  organisationId,
  userId,
  enabled = true,
}: {
  organisationId: number | undefined;
  userId?: number;
  enabled?: boolean;
}): UseQueryResult<ExpenseSubmissionWindow, Error> {
  return useQuery({
    queryKey: getExpenseSubmissionWindowQueryKey(organisationId ?? 0, userId),
    queryFn: () =>
      getExpenseSubmissionWindow({
        organisationId: organisationId as number,
        userId,
      }),
    enabled: enabled && organisationId != null && organisationId > 0,
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to create a new expense
 * @returns {UseMutationResult} Mutation result for creating an expense
 */
export function useCreateExpense(): UseMutationResult<Expense, Error, CreateExpenseRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createExpense,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-details'] });
      queryClient.invalidateQueries({ queryKey: ['expense-currencies'] });
      queryClient.invalidateQueries({ queryKey: ['expense-pending-totals'] });
    },
  });
}

/**
 * Hook to create a new pre-paid expense
 * @returns {UseMutationResult} Mutation result for creating a pre-paid expense
 */
export function useCreatePrepaidExpense(): UseMutationResult<
  Expense,
  Error,
  CreatePrepaidExpenseRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPrepaidExpense,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-details'] });
      queryClient.invalidateQueries({ queryKey: ['expense-currencies'] });
      queryClient.invalidateQueries({ queryKey: ['expense-pending-totals'] });
    },
  });
}

interface SubmitExpenseReceiptVariables extends SubmitExpenseReceiptRequest {
  expenseId: number;
}

/**
 * Hook to submit receipts for an approved pre-paid expense
 * @returns {UseMutationResult} Mutation result for submitting receipts
 */
export function useSubmitExpenseReceipt(): UseMutationResult<
  Expense,
  Error,
  SubmitExpenseReceiptVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expenseId, ...receiptData }) => submitExpenseReceipt(expenseId, receiptData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-details'] });
      queryClient.invalidateQueries({ queryKey: ['expense-pending-totals'] });
    },
  });
}

/**
 * Hook to cancel a pending expense
 * @returns {UseMutationResult} Mutation result for cancelling an expense
 */
export function useCancelExpense(): UseMutationResult<
  Expense,
  Error,
  { expenseId: number } & CancelExpenseRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelExpense,
    onSuccess: (data, { expenseId }) => {
      queryClient.setQueryData(['expense-details', expenseId], (oldData: Expense | undefined) => {
        if (oldData) {
          return { ...oldData, status: data.status };
        }
        return oldData;
      });
      queryClient.invalidateQueries({ queryKey: ['expense-details'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
}

interface PayExpenseMutationParams {
  expenseId: number;
  reimbursementMethod: ReimbursementMethod;
  walletCurrencyId?: number;
  teamId?: number;
}

/**
 * Hook to approve a single expense (wallet or payroll)
 * @returns {UseMutationResult} Mutation result for approving an expense
 */
export function usePayExpense(): UseMutationResult<
  PayExpenseResponse,
  Error,
  PayExpenseMutationParams
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expenseId, reimbursementMethod, walletCurrencyId, teamId }) =>
      payExpense(expenseId, {
        reimbursement_method: reimbursementMethod,
        ...(walletCurrencyId ? { wallet_currency_id: walletCurrencyId } : {}),
        ...(teamId ? { team_id: teamId } : {}),
      }),
    onSuccess: (data, { expenseId }) => {
      queryClient.setQueryData(['expense-details', expenseId], (oldData: Expense | undefined) => {
        if (oldData) {
          return {
            ...oldData,
            status: data.status,
            transaction_id: data.transaction_id,
            payment_date: data.payment_date,
          };
        }
        return oldData;
      });
      queryClient.invalidateQueries({ queryKey: ['expense-details'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      // Refresh the manager pending totals banner after approval.
      queryClient.invalidateQueries({ queryKey: ['expense-pending-totals'] });
      // Invalidate wallet and transaction queries as payment affects balances
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-balance'] });
      queryClient.invalidateQueries({ queryKey: ['expense-payer-wallets'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Hook to bulk decline multiple expenses
 * @returns {UseMutationResult} Mutation result for declining expenses
 */
export function useBulkDeclineExpenses(): UseMutationResult<Expense[], Error, BulkDeclineRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkDeclineExpenses,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense-details'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      // Refresh the manager pending totals banner after rejection.
      queryClient.invalidateQueries({ queryKey: ['expense-pending-totals'] });
    },
  });
}

/**
 * Hook to fetch pending expense totals grouped by currency
 * @param {ExpensePendingTotalsFilters} filters - Optional filters for pending totals
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for pending totals
 */
export function useExpensePendingTotals(
  filters: ExpensePendingTotalsFilters = {},
  enabled: boolean = true
): UseQueryResult<ExpensePendingTotalsData, Error> {
  const queryKey = [
    'expense-pending-totals',
    {
      organisation_id: filters.organisation_id,
      team_id: filters.team_id,
      currency: filters.currency,
      view_self_inputs: filters.view_self_inputs,
      sort: filters.sort,
    },
  ];

  return useQuery({
    queryKey,
    queryFn: () => getExpensePendingTotals(filters),
    enabled,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

/**
 * Hook to fetch approved expense totals grouped by category for a rolling window
 * @param {ExpenseCategoryBreakdownFilters} filters - Period, currency and scope filters
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for the category breakdown
 */
export function useExpenseCategoryBreakdown(
  filters: ExpenseCategoryBreakdownFilters,
  enabled: boolean = true
): UseQueryResult<ExpenseCategoryBreakdownData, Error> {
  const queryKey = [
    'expense-category-breakdown',
    {
      period: filters.period,
      currency: filters.currency,
      currency_id: filters.currency_id,
      organisation_id: filters.organisation_id,
      team_id: filters.team_id,
      view_self_inputs: filters.view_self_inputs,
    },
  ];

  return useQuery({
    queryKey,
    queryFn: () => getExpenseCategoryBreakdown(filters),
    enabled,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

/**
 * Hook to fetch approved expense totals grouped by month for a rolling window
 * @param {ExpenseApprovedTotalsFilters} filters - Period, currency and scope filters
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for the monthly approved totals
 */
export function useExpenseApprovedTotals(
  filters: ExpenseApprovedTotalsFilters,
  enabled: boolean = true
): UseQueryResult<ExpenseApprovedTotalsData, Error> {
  const queryKey = [
    'expense-approved-totals',
    {
      period: filters.period,
      year: filters.year,
      currency: filters.currency,
      currency_id: filters.currency_id,
      organisation_id: filters.organisation_id,
      team_id: filters.team_id,
      view_self_inputs: filters.view_self_inputs,
    },
  ];

  return useQuery({
    queryKey,
    queryFn: () => getExpenseApprovedTotals(filters),
    enabled,
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

/**
 * Hook to bulk pay multiple expenses
 * @returns {UseMutationResult} Mutation result for paying expenses in bulk
 */
export function useBulkPayExpenses(): UseMutationResult<BulkPayResponse, Error, BulkPayRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkPayExpenses,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense-details'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      // Invalidate wallet and transaction queries as bulk payment affects balances
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-balance'] });
      queryClient.invalidateQueries({ queryKey: ['expense-payer-wallets'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Wallets the paying organisation holds for an expense, used to pick a valid `wallet_currency_id`.
 */
export function useExpensePayerWallets(
  expenseId: number | null | undefined,
  enabled = true
): UseQueryResult<ExpensePayerWallet[], Error> {
  return useQuery({
    queryKey: ['expense-payer-wallets', expenseId],
    queryFn: () => getExpensePayerWallets(expenseId as number),
    enabled: enabled && !!expenseId,
    refetchOnWindowFocus: false,
  });
}

interface ReviewExpenseReceiptVariables extends ReviewExpenseReceiptRequest {
  expenseId: number;
}

/**
 * Hook to review submitted receipts for a pre-paid expense (approve or decline)
 * @returns {UseMutationResult} Mutation result for reviewing expense receipts
 */
export function useReviewExpenseReceipt(): UseMutationResult<
  Expense,
  Error,
  ReviewExpenseReceiptVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expenseId, ...reviewData }) => reviewExpenseReceipt(expenseId, reviewData),
    onSuccess: (data, { expenseId }) => {
      queryClient.setQueryData(['expense-details', expenseId], (oldData: Expense | undefined) => {
        if (oldData) return { ...oldData, ...data };
        return oldData;
      });
      queryClient.invalidateQueries({ queryKey: ['expense-details'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['expense-pending-totals'] });
      // Overspend approval dispatches a supplementary payment
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-balance'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Hook to send a receipt submission reminder for an approved pre-paid expense
 * @returns {UseMutationResult} Mutation result for sending the reminder
 */
export function useSendReceiptReminder(): UseMutationResult<void, Error, number> {
  return useMutation({
    mutationFn: (expenseId) => sendReceiptReminder(expenseId),
  });
}

/**
 * Hook to export expense category breakdown as a CSV file
 * @returns {UseMutationResult} Mutation result that triggers a browser CSV download on success
 */
export function useExpenseCategoryBreakdownExport(): UseMutationResult<
  Blob,
  Error,
  ExpenseCategoryBreakdownFilters
> {
  return useMutation({
    mutationFn: getExpenseCategoryBreakdownExport,
    onSuccess: (blob, filters) => {
      const { from, to } = getExpensePeriodDateRange(filters.period);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `category-breakdown-from-${from}-to-${to}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}
